import type { RouteLocationNormalized, RouteRecordNormalized } from 'vue-router';
import type { EntitlementChecker } from '#layers/access/public';
import type { MatchedRoute } from './entitlement-gate.decision';
import { storeToRefs } from 'pinia';
import { defineNuxtRouteMiddleware, useRouter } from '#app/composables/router';
import { useAuth, useEntitlement, usePermissionsStore } from '#layers/access/public';
import { resolveSettingsNavigation } from '#layers/settings/public';
import { decideEntitlementRedirect } from './entitlement-gate.decision';

/**
 * Gates deep-link / hard navigation to entitlement-gated workspace routes. Registered after
 * `switch-workspace`, which publishes the runtime whose cache holds the permissions this reads. The redirect logic lives in
 * `decideEntitlementRedirect`; this wires it to the permissions store and the router.
 */
export default defineNuxtRouteMiddleware((to) => {
	const permissions = usePermissionsStore();
	const { can } = useEntitlement();
	// Resolved eagerly during boot (auth.ensureInitialized()), awaited by the `private` global
	// middleware which runs before this — so org-admin state is known by the time the gate runs.
	const { isOrganizationAdmin } = storeToRefs(useAuth());
	const router = useRouter();

	return decideEntitlementRedirect({
		ready: permissions.isReady,
		matched: toGateMatched(to),
		can,
		params: to.params,
		isOrganizationAdmin: isOrganizationAdmin.value,
		workspaceParam: typeof to.params.workspace === 'string' ? to.params.workspace : '',
		resolveFirstVisibleSettingsRoute: () => firstVisibleSettingsRoute(router.getRoutes(), can, isOrganizationAdmin.value),
	});
});

/** Reduce the matched chain to the name + gating meta the decision needs. */
function toGateMatched(to: RouteLocationNormalized): MatchedRoute[] {
	return to.matched.map((record) => ({
		name: typeof record.name === 'string' ? record.name : undefined,
		meta: record.meta,
	}));
}

/** The first settings route the current user may see, mirroring the settings sidebar order. */
function firstVisibleSettingsRoute(routes: readonly RouteRecordNormalized[], can: EntitlementChecker, isOrganizationAdmin: boolean): string | undefined {
	const { groups, links } = resolveSettingsNavigation({ routes, can, isOrganizationAdmin });
	const firstGroup = groups[0];
	if (!firstGroup) return undefined;
	return links[firstGroup.id]?.[0]?.id;
}
